import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface RejectDraftDialogProps {
  open: boolean;
  documentName?: string;
  title?: string;
  description?: string;
  isSubmitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (comment: string) => Promise<void> | void;
}

export function RejectDraftDialog({
  open,
  documentName,
  title = 'Reject draft copy',
  description,
  isSubmitting = false,
  onOpenChange,
  onConfirm,
}: RejectDraftDialogProps) {
  const [comment, setComment] = useState('');

  const handleOpenChange = (next: boolean) => {
    if (!next) setComment('');
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    await onConfirm(comment.trim());
    setComment('');
  };

  const resolvedDescription =
    description ??
    (documentName
      ? `Reject "${documentName}" and optionally add a comment for your tax preparer.`
      : 'Optionally add a comment explaining why you are rejecting this draft.');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{resolvedDescription}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="rejection-comment">Comment (optional)</Label>
          <Textarea
            id="rejection-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Reason for rejection…"
            rows={4}
            disabled={isSubmitting}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
            className="gap-2"
          >
            <X className="h-4 w-4" />
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            loading={isSubmitting}
            loadingLabel="Rejecting…"
          >
            Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
